import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';

import { useAuth } from '@/hooks/auth-context';
import { api, type ActivityResult } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

// Badge freshness cadence (issue #395 AC5): new activity shows in the
// tab badges within ~30s.
export const ACTIVITY_POLL_MS = 30_000;

// The ONE app-wide badge poll (issue #395): GET /me/activity carries
// both badge counts in a single query, replacing the per-screen fetches
// (hooks/use-incoming-reveal-request-count.ts and
// hooks/use-unread-message-count.ts, both deleted with this). One
// queryKey, so every consumer -- the tab badges in app/(app)/_layout.tsx,
// the threads screen's banner, the settings screen -- shares one cache
// entry and TanStack dedupes into a single in-flight request no matter
// how many surfaces are mounted.
//
// 304s are the expected steady state: the cached etag goes back out as
// If-None-Match and the server answers 304 with no body. getActivity
// turns that into null and this queryFn keeps the cached counts -- never
// an error. Poll failure (offline, 5xx) is the same story with
// retry: false: TanStack keeps the last data, nothing throws at the
// screen, and the next poll IS the retry (AC6). Visibility is TanStack's
// own: refetchIntervalInBackground defaults to false, and lib/network.ts
// wires focusManager to AppState so "backgrounded" is real on native
// too -- nothing polls while hidden or backgrounded.
export function activityQuery(queryClient: QueryClient) {
  return {
    queryKey: queryKeys.activity(),
    queryFn: async (): Promise<ActivityResult> => {
      const cached = queryClient.getQueryData<ActivityResult>(queryKeys.activity());
      const result = await api.getActivity(cached?.etag);
      if (result === null) {
        // 304 Not Modified -- only reachable when an etag was sent,
        // which only happens when `cached` existed. Keep exactly those.
        if (!cached) throw new Error('unexpected empty activity response');
        return cached;
      }
      return result;
    },
    retry: false,
  };
}

export function useActivity() {
  const queryClient = useQueryClient();
  const auth = useAuth();

  return useQuery({
    ...activityQuery(queryClient),
    // Defensive, same as the badge hooks this replaces: (app) mounts
    // only behind the auth gate.
    enabled: auth.status === 'signed-up',
    refetchInterval: ACTIVITY_POLL_MS,
  });
}
